import { useIntl } from 'react-intl';
import { Image, OverlayTrigger, Popover } from 'react-bootstrap';

import { CloseIcon } from 'assets/icons';
import AvatarNameColor from 'components/UI/AvatarNameColor';
import { convertComparisonOperatorToSymbol } from 'utils/ComparisonOperators';

interface IActiveFiltersDisplay {
  activeFilters: ITemplatePageFilters[];
  clearFilters: (filtersLabels: IFilterLabel[]) => void;
}

const ActiveFiltersDisplay = ({ activeFilters, clearFilters }: IActiveFiltersDisplay) => {
  const { formatMessage } = useIntl();
  const displayFilter = (filter: ITemplatePageFilters, filterData: ISelectOption) => {
    const getFilterValueFromLabel = (label: string) => {
      const filter = activeFilters.find((filter) => filter.filterLabel === label);
      return filter?.filterValue?.[0] || '';
    };
    switch (filter.filterLabel) {
      case 'alerts':
        return (
          <span key={filterData.label}>
            <span className="me-2">{filterData.icon}</span>
            {filterData.label}
          </span>
        );

      case 'timeLogType':
        return (
          <span key={filterData.label}>
            {`${filterData.label} ${formatMessage({
              id: 'from'
            })} ${getFilterValueFromLabel('timeLogMin')} ${formatMessage({
              id: 'minutes'
            })} ${formatMessage({ id: 'to' })} ${getFilterValueFromLabel(
              'timeLogMax'
            )} ${formatMessage({ id: 'minutes' })}`}
          </span>
        );

      case 'medicalTxDaysOperator':
        return (
          <span key={filterData.label}>
            {`${convertComparisonOperatorToSymbol(filterData.value)} ${getFilterValueFromLabel(
              'medicalTxDaysValue'
            )}`}
          </span>
        );

      case 'rollingTdayOperator':
        return (
          <span key={filterData.label}>
            {`${convertComparisonOperatorToSymbol(filterData.value)} ${getFilterValueFromLabel(
              'rollingTdayValue'
            )}`}
          </span>
        );
      case 'taskTime':
        return <span key={filterData.label}>{filterData.label}</span>;
      case 'assignUsers':
        return (
          <span key={filterData.label}>
            <AvatarNameColor
              name={filterData.label}
              size={30}
              className={filterData.label ? 'me-2' : ''}
            />
            {filterData.label}
          </span>
        );

      default:
        return (
          <span key={filterData.label}>
            {filter.filterLabel === 'tags' && (
              <Image
                roundedCircle={false}
                src={filterData.icon}
                className={filterData.label ? 'me-2' : ''}
                height="25px"
                width="25px"
              />
            )}
            {filterData.label}
          </span>
        );
    }
  };

  const getCitcKey = (filterLabel: IFilterLabel) => {
    switch (filterLabel) {
      case 'assignUsers':
        return 'assignedTo';
      case 'timeLogType':
        return 'clinicalTimeKey';
      case 'alerts':
        return 'alertsKey';
      case 'patientStatuses':
        return 'patientStatusKey';
      case 'patientCareProgram':
        return 'programsKey';
      case 'medicalTxDaysOperator':
        return 'transmissionDaysKey';
      case 'subgroups':
        return 'groupsKey';
      case 'rollingTdayOperator':
        return 'daysLeftKey';
      case 'taskTime':
        return 'timeKey';
      case 'taskStatus':
        return 'taskStatusKey';
      case 'status':
        return 'statusKey';
      case 'group':
        return 'groupKey';
      case 'requesterName':
        return 'requesterKey';
      case 'agent':
        return 'agentKey';
      case 'affectedIMEI':
        return 'affectedImeiKey';
      case 'reasonForReplacement':
        return 'reasonForReplacementKey';
      case 'component':
        return 'componentKey';
      default:
        return filterLabel;
    }
  };

  return (
    <div className="filterDisplay">
      {activeFilters
        ?.filter(
          (filter) =>
            filter.filterLabel !== 'timeLogMin' &&
            filter.filterLabel !== 'timeLogMax' &&
            filter.filterLabel !== 'medicalTxDaysValue' &&
            filter.filterLabel !== 'rollingTdayValue'
        )
        .map((filter) => {
          if (filter.filterData) {
            return (
              <div
                key={filter.filterLabel}
                className="position-relative me-2"
                data-test={filter.filterLabel + 'FilterDisplay'}
              >
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Popover id="popover-basic">
                      <Popover.Body className="filterPopoverContent">
                        {filter.filterData.map((data: any) => displayFilter(filter, data))}
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <span className="filterDisplayMessage">
                    {filter.filterLabel === 'taskTime' && filter.filterData.length === 1
                      ? filter.filterData[0].label
                      : formatMessage({
                          id: getCitcKey(filter.filterLabel)
                        })}
                    {filter.filterLabel !== 'taskTime' && <>&nbsp; ({filter.filterData.length})</>}
                  </span>
                </OverlayTrigger>
                <CloseIcon
                  onClick={() => {
                    if (filter.filterLabel === 'timeLogType') {
                      clearFilters(['timeLogType', 'timeLogMin', 'timeLogMax']);
                    } else if (filter.filterLabel === 'medicalTxDaysOperator') {
                      clearFilters(['medicalTxDaysOperator', 'medicalTxDaysValue']);
                    } else if (filter.filterLabel === 'rollingTdayOperator') {
                      clearFilters(['rollingTdayOperator', 'rollingTdayValue']);
                    } else {
                      clearFilters([filter.filterLabel]);
                    }
                  }}
                  style={{ fontSize: '20px' }}
                  className="icon filterDisplayCloseIcon"
                />
              </div>
            );
          }
        })}
    </div>
  );
};

export default ActiveFiltersDisplay;
